/**
 * Open Food Facts API Integration
 * ================================
 * Fetches food nutrition data from Open Food Facts database
 * API Documentation: https://world.openfoodfacts.org/data
 */

const API_BASE_URL = 'https://world.openfoodfacts.org/api/v2';

/**
 * Search food by barcode
 * @param {string} barcode - Product barcode (EAN-13, UPC, etc.)
 * @returns {Promise<Object|null>} Food data or null if not found
 */
export const searchByBarcode = async (barcode) => {
  try {
    const response = await fetch(`${API_BASE_URL}/product/${barcode}.json`);
    const data = await response.json();

    if (data.status === 1 && data.product) {
      return parseProductData(data.product);
    }
    return null;
  } catch (error) {
    console.error('Error fetching from Open Food Facts:', error);
    return null;
  }
};

/**
 * Parse Open Food Facts product data to our food format
 * @param {Object} product - Raw product data from API
 * @returns {Object} Parsed food data
 */
const parseProductData = (product) => {
  const n = product.nutriments || {};

  // Per-100g values
  const cal100  = Math.round(n['energy-kcal_100g'] || n.energy_kcal_100g || 0);
  const prot100 = parseFloat((n.proteins_100g || 0).toFixed(1));
  const carb100 = parseFloat((n.carbohydrates_100g || 0).toFixed(1));
  const fat100  = parseFloat((n.fat_100g || 0).toFixed(1));
  const fiber100 = parseFloat((n.fiber_100g || 0).toFixed(1));
  const sugar100 = parseFloat((n.sugars_100g || 0).toFixed(1));
  const satfat100 = parseFloat((n['saturated-fat_100g'] || 0).toFixed(1));
  // OFacts sodium and potassium are in g/100g — convert to mg
  const sodium100 = parseFloat(((n.sodium_100g || 0) * 1000).toFixed(1));
  const pot100    = parseFloat(((n.potassium_100g || 0) * 1000).toFixed(1));

  const base = {
    serving_id: '100g',
    description: '100 g',
    metric_amount: 100,
    metric_serving_amount: 100,
    metric_unit: 'g',
    metric_serving_unit: 'g',
    amount: 100,
    unit: 'g',
    is_default: true,
    calories: cal100,
    protein: prot100,
    carbs: carb100,
    fat: fat100,
    saturated_fat: satfat100,
    polyunsaturated_fat: 0,
    monounsaturated_fat: 0,
    cholesterol: 0,
    sodium: sodium100,
    potassium: pot100,
    fiber: fiber100,
    sugar: sugar100,
    vitamin_a: 0, vitamin_c: 0, calcium: 0, iron: 0,
  };

  const per1g = {
    ...base,
    serving_id: '1g',
    description: '1 g',
    metric_amount: 1,
    metric_serving_amount: 1,
    amount: 1,
    is_default: false,
    calories: parseFloat((cal100 / 100).toFixed(2)),
    protein: parseFloat((prot100 / 100).toFixed(3)),
    carbs: parseFloat((carb100 / 100).toFixed(3)),
    fat: parseFloat((fat100 / 100).toFixed(3)),
    saturated_fat: parseFloat((satfat100 / 100).toFixed(3)),
    sodium: parseFloat((sodium100 / 100).toFixed(3)),
    potassium: parseFloat((pot100 / 100).toFixed(3)),
    fiber: parseFloat((fiber100 / 100).toFixed(3)),
    sugar: parseFloat((sugar100 / 100).toFixed(3)),
  };

  return {
    fatsecret_id: null,
    off_id: product.code || '',
    source: 'openfoodfacts',
    food_type: 'openfoodfacts',
    name: product.product_name_pt || product.product_name || 'Unknown Product',
    brand: product.brands || '',
    image_url: product.image_url || product.image_front_url || '',
    image_url_small: product.image_small_url || product.image_front_small_url || '',
    serving_size: 100,
    serving_unit: 'g',
    serving_description: '100 g',
    serving_hint: '100 g',
    calories: cal100,
    protein: prot100,
    carbs: carb100,
    fat: fat100,
    saturated_fat: satfat100,
    polyunsaturated_fat: 0,
    monounsaturated_fat: 0,
    cholesterol: 0,
    sodium: sodium100,
    potassium: pot100,
    fiber: fiber100,
    sugar: sugar100,
    vitamin_a: 0, vitamin_c: 0, calcium: 0, iron: 0,
    servings: [base, per1g],
    preferences: [],
    allergens: [],
    sub_categories: [],
  };
};

/**
 * Search food by text query
 * @param {string} query - Search query
 * @param {number} page - Page number (default 1)
 * @returns {Promise<Array>} Array of food items
 */
export const searchByText = async (query, page = 1) => {
  try {
    const url = `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(query)}&action=process&json=1&page=${page}&page_size=10&cc=pt&lc=pt&fields=product_name,product_name_pt,brands,nutriments,code,image_url,image_front_url,image_small_url,image_front_small_url`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.products && data.products.length > 0) {
      return data.products
        .map(parseProductData)
        .filter(f => f.name !== 'Unknown Product');
    }
    return [];
  } catch (error) {
    console.error('Open Food Facts search error:', error);
    return [];
  }
};

/**
 * Validate barcode format
 * @param {string} barcode - Barcode string
 * @returns {boolean} True if valid format
 */
export const isValidBarcode = (barcode) => {
  // Check if it's a number and has valid length (8, 12, 13, or 14 digits)
  const cleaned = barcode.replace(/\s/g, '');
  return /^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(cleaned);
};
